export interface CalendarCell {
  date: string;
  day: number;
  enabled: boolean;
}

export interface CalendarMonth {
  /** `YYYY-MM` */
  key: string;
  weeks: (CalendarCell | null)[][];
}

const toIso = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const closeWeek = (
  month: CalendarMonth,
  week: (CalendarCell | null)[],
): void => {
  if (week.length === 0) return;
  while (week.length < 7) week.push(null);
  month.weeks.push(week);
};

/**
 * Lays the days out as Sunday-first weeks, grouped by month. Each month starts
 * on a fresh row so a week that crosses a month boundary is split in two,
 * padded with nulls on either side.
 */
export const buildCalendarGrid = (
  days: { date: string; enabled: boolean }[],
): { monthLabel: string; months: CalendarMonth[] } => {
  // ISO dates sort lexicographically; callers may pass days out of order.
  const sortedDays = [...days].sort((a, b) => a.date.localeCompare(b.date));
  const firstDay = sortedDays[0];
  const lastDay = sortedDays[sortedDays.length - 1];
  if (!firstDay || !lastDay) return { monthLabel: '', months: [] };

  const firstDate = new Date(`${firstDay.date}T00:00:00`);
  const lastDate = new Date(`${lastDay.date}T00:00:00`);

  const sameMonth =
    firstDate.getFullYear() === lastDate.getFullYear() &&
    firstDate.getMonth() === lastDate.getMonth();

  const monthLabel = sameMonth
    ? firstDate.toLocaleDateString('en', { month: 'long', year: 'numeric' })
    : `${firstDate.toLocaleDateString('en', { month: 'long', year: 'numeric' })} – ${lastDate.toLocaleDateString('en', { month: 'long', year: 'numeric' })}`;

  const dayMap = new Map(days.map((d) => [d.date, d]));

  const months: CalendarMonth[] = [];
  let currentWeek: (CalendarCell | null)[] = [];

  const cursor = new Date(firstDate);
  while (cursor <= lastDate) {
    const iso = toIso(cursor);
    const monthKey = iso.slice(0, 7);
    let currentMonth = months.at(-1);

    if (currentMonth?.key !== monthKey) {
      if (currentMonth) closeWeek(currentMonth, currentWeek);
      currentMonth = { key: monthKey, weeks: [] };
      months.push(currentMonth);
      currentWeek = Array.from({ length: cursor.getDay() }, () => null);
    }

    const dayData = dayMap.get(iso);
    currentWeek.push(
      dayData
        ? { date: iso, day: cursor.getDate(), enabled: dayData.enabled }
        : null,
    );

    if (currentWeek.length === 7) {
      closeWeek(currentMonth, currentWeek);
      currentWeek = [];
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  const lastMonth = months.at(-1);
  if (lastMonth) closeWeek(lastMonth, currentWeek);

  return { monthLabel, months };
};
